import React, { useEffect, useState } from "react";
import axios from "axios";
import { Star, Users, MessageCircle } from "lucide-react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { apiurl } from "../config/config";
import { Bar } from "react-chartjs-2";
import { useParams } from "react-router-dom";
import Layout, { Container } from "../components/layout";
import { s } from "framer-motion/client";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const SurveyReportPage = () => {
  const { surveyId } = useParams();
  const [report, setReport] = useState(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const res = await axios.get(`${apiurl}/api/survey/report/${surveyId}`);
        if (res.data.success) setReport(res.data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchReport();
  }, [surveyId]);

  if (!report)
    return (
      <div className="p-6 text-center text-gray-600">
        Loading survey report...
      </div>
    );

  const { totalResponses, overallRatingAvg, summary, detailedResponses } =
    report;

  return (
    <Layout>
      <Container overflow={"overflow-scroll"}>
        <div className="min-h-screen bg-gray-50 p-6">
          <div className="mx-auto">
            {/* Header */}
            <div className="mb-10 text-center">
              <h1 className="text-4xl font-bold text-gray-900">
                {report?.survey?.title} Survey Report
              </h1>
              <p className="mt-2 text-gray-600">
                {report?.survey?.description}
              </p>
              <div className="flex justify-center items-center gap-6 mt-3 text-gray-600">
                <p>
                  <Users className="inline-block mr-1 text-blue-500" />
                  Total Responses:{" "}
                  <span className="font-semibold">{totalResponses}</span>
                </p>
                {overallRatingAvg && (
                  <p className="flex items-center gap-1">
                    <Star className="text-yellow-400" /> Overall Avg:{" "}
                    <span className="font-semibold">{overallRatingAvg}</span>
                  </p>
                )}
              </div>
            </div>


<div className="grid grid-cols-2 gap-4">

            {/* Questions */}
            {Object.values(summary).map((q, idx) => (
              <div
                key={idx}
                className="bg-white shadow-lg rounded-xl p-6 mb-6 border-l-4 border-yellow-400"
              >
                <h2 className="text-xl font-semibold text-gray-800 mb-4">
                  {q.questionText}
                </h2>

                {/* YES/NO or Multiple Choice */}
                {(q.type === "yesno" || q.type === "multipleChoice") && (
                  <div className="w-full max-w-xl">
                    <Bar
                      data={{
                        labels: Object.keys(q.counts),
                        datasets: [
                          {
                            label: "Responses",
                            data: Object.values(q.counts),
                            backgroundColor: "rgba(253, 224, 71, 0.8)",
                            borderRadius: 8,
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        plugins: {
                          legend: { display: false },
                          title: {
                            display: true,
                            text: `Top Answer: ${q.topAnswer}`,
                            font: { size: 16 },
                          },
                        },
                        scales: {
                          y: {
                            beginAtZero: true,
                            ticks: {
                              stepSize: 1,
                              font: { size: 14 },
                              color: "#374151",
                            },
                          },
                          x: {
                            ticks: { font: { size: 14 }, color: "#374151" },
                          },
                        },
                      }}
                    />
                    <div className="mt-3 flex flex-wrap gap-4">
                      {Object.entries(q.counts).map(([ans, count]) => (
                        <div
                          key={ans}
                          className="px-4 py-2 bg-yellow-100 rounded-full text-gray-700 font-medium flex items-center gap-2 shadow-sm"
                        >
                          {ans}: <span className="font-bold">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Rating */}
                {q.type === "rating" && (
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-2">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          size={28}
                          className={
                            i < Math.round(q.avg || 0)
                              ? "text-yellow-400"
                              : "text-gray-300"
                          }
                        />
                      ))}
                      <p className="text-gray-600 font-medium ml-2">
                        Average: {q.avg}
                      </p>
                    </div>
                    <p className="text-gray-500 text-sm">
                      Total responses:{" "}
                      {Object.values(q.counts).reduce((a, b) => a + b, 0)}
                    </p>
                  </div>
                )}

                {/* Text Answers */}
                {q.type === "text" && (
                  <div className="mt-3">
                    {q.textAnswers.length > 0 ? (
                      <div className="max-h-52 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
                        {q.textAnswers.map((ans, idx) => (
                          <div
                            key={idx}
                            className="mb-2 px-2 py-1 bg-yellow-50 rounded-lg flex items-center gap-2"
                          >
                            <MessageCircle
                              size={16}
                              className="text-yellow-500"
                            />
                            <span className="text-gray-700">{ans}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-400">No responses yet</p>
                    )}
                  </div>
                )}
              </div>
            ))}
</div>

            <div className="mt-10">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">
                Employee Feedback
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {detailedResponses.map((resp, idx) => (
                  <div
                    key={idx}
                    className="bg-white shadow-lg rounded-xl p-5 border border-gray-200 hover:shadow-2xl transition-shadow"
                  >
                    {/* Header */}
                    <div className="flex justify-between items-center mb-3">
                      <p className="font-semibold text-gray-800">
                        {resp.respondent.employeeName}
                      </p>
                      <span className="text-gray-500 text-sm">
                        {resp.respondent.employeeCode}
                      </span>
                    </div>

                    {/* Overall Rating */}
                    <div className="flex items-center mb-3">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          size={20}
                          className={
                            i < Math.round(resp.overallRating || 0)
                              ? "text-yellow-400"
                              : "text-gray-300"
                          }
                        />
                      ))}
                      <span className="ml-2 text-gray-600 font-medium">
                        {resp.overallRating}
                      </span>
                    </div>

                    {/* Text Answer */}
                    <div className="bg-gray-50 p-3 rounded-lg text-gray-700">
                      <MessageCircle
                        size={16}
                        className="inline-block mr-2 text-yellow-500"
                      />
                      <span>{resp.answers["Q1005"]}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Container>
    </Layout>
  );
};

export default SurveyReportPage;
